import { useRef, useState, useEffect, useCallback } from "react";

const AUDIO_EXCLUSIVE = "ulc-audio-play";

/** Minimal HTML5 audio player hook mirroring the Mobile App's expo-audio usage. */
export function useAudio() {
  const ref = useRef(null);
  if (!ref.current && typeof Audio !== "undefined") {
    ref.current = new Audio();
    ref.current.preload = "auto";
  }
  const [status, setStatus] = useState({ playing: false, currentTime: 0, duration: 0, isBuffering: false, didJustFinish: false });

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const upd = (extra = {}) =>
      setStatus((s) => ({ ...s, playing: !a.paused, currentTime: a.currentTime || 0, duration: a.duration || 0, ...extra }));
    const onTime = () => upd({ didJustFinish: false });
    const onPlay = () => upd();
    const onPause = () => upd();
    const onWaiting = () => upd({ isBuffering: true });
    const onPlaying = () => upd({ isBuffering: false });
    const onLoaded = () => upd({ isBuffering: false });
    const onEnded = () => upd({ didJustFinish: true, playing: false });
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => () => ref.current?.pause(), []);

  // Only one audio source at a time across the app (song page vs global player).
  useEffect(() => {
    const a = ref.current;
    const onPlay = () => window.dispatchEvent(new CustomEvent(AUDIO_EXCLUSIVE, { detail: a }));
    const onOther = (e) => { if (e.detail !== a) a.pause(); };
    a.addEventListener("play", onPlay);
    window.addEventListener(AUDIO_EXCLUSIVE, onOther);
    return () => { a.removeEventListener("play", onPlay); window.removeEventListener(AUDIO_EXCLUSIVE, onOther); };
  }, []);

  const replace = useCallback((uri) => {
    const a = ref.current;
    if (!a) return;
    a.src = uri;
    a.load();
  }, []);
  const play = useCallback(() => ref.current?.play().catch(() => {}), []);
  const pause = useCallback(() => ref.current?.pause(), []);
  const seekTo = useCallback((sec) => {
    if (ref.current) ref.current.currentTime = sec;
  }, []);

  return { status, replace, play, pause, seekTo };
}
